import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import storageService from '../../services/storageService';

const ImageUpload = ({ 
  value = [], 
  onChange, 
  maxFiles = 1, 
  maxSizeMB = 10, 
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'],
  preview = true,
  dragAndDrop = true,
  label,
  placeholder = "Click to upload or drag and drop",
  error,
  required = false,
  disabled = false,
  className = "",
  // Supabase integration props
  bucketName,
  userId,
  autoUpload = true,
  onUploadComplete,
  onUploadError
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const validateFile = useCallback((file) => {
    const errors = [];
    
    // Check file type
    if (!acceptedTypes?.includes(file?.type)) {
      errors?.push(`Invalid file type. Accepted: ${acceptedTypes?.join(', ')}`);
    }
    
    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file?.size > maxSizeBytes) {
      errors?.push(`File size must be less than ${maxSizeMB}MB`);
    }
    
    // Additional Supabase bucket validation
    if (bucketName && userId) {
      try {
        storageService?.validateFile(file, bucketName);
      } catch (validationError) {
        errors?.push(validationError?.message);
      }
    }
    
    return errors;
  }, [acceptedTypes, maxSizeMB, bucketName, userId]);

  const uploadToSupabase = useCallback(async (file, fileId) => {
    if (!bucketName || !userId) {
      throw new Error('Bucket name and user ID required for upload');
    }

    try {
      const result = await storageService?.uploadFile(file, bucketName, userId, {
        onProgress: (progress) => {
          setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
        }
      });

      return result;
    } catch (uploadError) {
      console.error('Supabase upload error:', uploadError);
      throw uploadError;
    }
  }, [bucketName, userId]);

  const processFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList);
    const validFiles = [];
    const allErrors = [];

    // Check if we exceed max files limit
    if (value?.length + files?.length > maxFiles) {
      allErrors?.push(`Maximum ${maxFiles} files allowed. Current: ${value?.length}, Adding: ${files?.length}`);
      return { validFiles: [], errors: allErrors };
    }

    for (const file of files) {
      const errors = validateFile(file);
      
      if (errors?.length === 0) {
        const fileId = `${file?.name}-${Date.now()}-${Math.random()}`;
        
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        
        const fileObject = {
          file,
          id: fileId,
          name: file?.name,
          size: file?.size,
          type: file?.type,
          previewUrl,
          uploaded: false,
          uploadProgress: 0
        };

        // Auto-upload to Supabase if enabled
        if (autoUpload && bucketName && userId) {
          try {
            setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
            
            const uploadResult = await uploadToSupabase(file, fileId);
            
            fileObject.uploaded = true;
            fileObject.uploadProgress = 100;
            fileObject.storagePath = uploadResult?.path;
            fileObject.bucket = uploadResult?.bucket;
            fileObject.compressed = uploadResult?.compressed;
            
            // Get file URL for preview
            const fileUrl = await storageService?.getFileUrl(uploadResult?.path, bucketName);
            fileObject.fileUrl = fileUrl;
            
            if (onUploadComplete) {
              onUploadComplete(fileObject, uploadResult);
            }
          } catch (uploadError) {
            fileObject.uploadError = uploadError?.message;
            
            if (onUploadError) {
              onUploadError(fileObject, uploadError);
            }
            
            allErrors?.push(`${file?.name}: ${uploadError?.message}`);
          }
        }
        
        validFiles?.push(fileObject);
      } else {
        allErrors?.push(...errors);
      }
    }

    return { validFiles, errors: allErrors };
  }, [value, maxFiles, validateFile, autoUpload, bucketName, userId, uploadToSupabase, onUploadComplete, onUploadError]);

  const handleFileSelect = useCallback(async (files) => {
    if (disabled) return;
    
    setUploading(true);
    
    const { validFiles, errors } = await processFiles(files);
    
    if (errors?.length > 0) {
      console.error('File validation/upload errors:', errors);
    }
    
    if (validFiles?.length > 0) {
      const newFiles = [...(value || []), ...validFiles];
      onChange?.(newFiles);
    }
    
    setUploading(false);
  }, [disabled, processFiles, value, onChange]);

  const handleInputChange = useCallback((e) => {
    const files = e?.target?.files;
    if (files?.length > 0) {
      handleFileSelect(files);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleFileSelect(files);
    }
  }, [disabled, handleFileSelect]);

  const handleRemoveFile = useCallback(async (fileId) => {
    const fileToRemove = value?.find(file => file?.id === fileId);
    
    // Delete from Supabase if uploaded
    if (fileToRemove?.uploaded && fileToRemove?.storagePath && bucketName) {
      try {
        await storageService?.deleteFile(fileToRemove?.storagePath, bucketName);
      } catch (deleteError) {
        console.error('Failed to delete file from storage:', deleteError);
      }
    }
    
    const updatedFiles = value?.filter(file => file?.id !== fileId) || [];
    
    // Clean up preview URLs to prevent memory leaks
    if (fileToRemove?.previewUrl) {
      URL.revokeObjectURL(fileToRemove?.previewUrl);
    }
    
    onChange?.(updatedFiles);
  }, [value, bucketName, onChange]);

  const handleClickUpload = useCallback(() => {
    if (disabled) return;
    fileInputRef?.current?.click();
  }, [disabled]);

  // Calculate remaining slots
  const remainingSlots = maxFiles - (value?.length || 0);
  const canAddMore = remainingSlots > 0 && !disabled;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Label */}
      {label && (
        <div className="flex items-center gap-1 mb-1">
          <span className="text-sm font-medium text-foreground">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </div>
      )}
      {/* Upload Area */}
      {canAddMore && (
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-6 transition-all duration-200
            ${isDragOver 
              ? 'border-primary bg-primary/5 ring-2 ring-primary ring-opacity-20' :'border-gray-300 hover:border-primary/50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            ${error ? 'border-red-300 bg-red-50' : ''}
          `}
          onClick={handleClickUpload}
          onDragOver={dragAndDrop ? handleDragOver : undefined}
          onDragLeave={dragAndDrop ? handleDragLeave : undefined}
          onDrop={dragAndDrop ? handleDrop : undefined}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple={maxFiles > 1}
            accept={acceptedTypes?.join(',')}
            onChange={handleInputChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={disabled}
          />
          
          <div className="flex flex-col items-center text-center">
            <div className={`
              p-3 rounded-full mb-3 transition-colors
              ${isDragOver ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}
            `}>
              {uploading ? (
                <Icon name="Loader2" size={24} className="animate-spin" />
              ) : (
                <Icon name="Upload" size={24} />
              )}
            </div>
            
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {uploading ? 'Processing & Uploading...' : placeholder}
              </p>
              <p className="text-xs text-muted-foreground">
                {maxFiles > 1 
                  ? `Up to ${remainingSlots} more files, max ${maxSizeMB}MB each`
                  : `Max ${maxSizeMB}MB`
                }
              </p>
              <p className="text-xs text-muted-foreground">
                Supports: {acceptedTypes?.map(type => type?.split('/')?.pop()?.toUpperCase())?.join(', ')}
              </p>
              {bucketName && (
                <p className="text-xs text-blue-600 mt-1">
                  Auto-upload enabled
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* File Previews */}
      {preview && value?.length > 0 && (
        <div className="space-y-2">
          <AnimatePresence>
            {value?.map((file) => {
              const progress = uploadProgress?.[file?.id] || file?.uploadProgress || 0;
              const isUploading = progress > 0 && progress < 100 && !file?.uploaded;
              
              return (
                <motion.div
                  key={file?.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg relative overflow-hidden"
                >
                  {/* Upload Progress Bar */}
                  {isUploading && (
                    <div 
                      className="absolute left-0 top-0 h-full bg-blue-100 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  )}
                  {/* Preview Image */}
                  {(file?.previewUrl || file?.fileUrl) && (
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-200 relative z-10">
                      <img 
                        src={file?.fileUrl || file?.previewUrl} 
                        alt={file?.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  {/* File Info */}
                  <div className="flex-1 min-w-0 relative z-10">
                    <p className="text-sm font-medium text-foreground truncate">
                      {file?.name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{(file?.size / 1024 / 1024)?.toFixed(2)} MB</span>
                      {file?.compressed && (
                        <span className="text-green-600">• Compressed</span>
                      )}
                      {isUploading && (
                        <span className="text-blue-600">• {progress}%</span>
                      )}
                    </div>
                    {file?.uploadError && (
                      <p className="text-xs text-red-500 mt-1">
                        {file?.uploadError}
                      </p>
                    )}
                  </div>
                  {/* Upload Status */}
                  <div className="flex items-center gap-2 relative z-10">
                    {isUploading ? (
                      <Icon name="Loader2" size={16} className="text-blue-500 animate-spin" />
                    ) : file?.uploaded ? (
                      <Icon name="CheckCircle" size={16} className="text-green-500" />
                    ) : file?.uploadError ? (
                      <Icon name="AlertCircle" size={16} className="text-red-500" />
                    ) : (
                      <Icon name="Clock" size={16} className="text-yellow-500" />
                    )}
                    
                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemoveFile(file?.id)}
                      className="p-1 hover:bg-red-100 rounded-full transition-colors"
                      disabled={disabled || isUploading}
                    >
                      <Icon name="X" size={16} className="text-red-500" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
      {/* File Count Info */}
      {maxFiles > 1 && (
        <div className="text-xs text-muted-foreground">
          {value?.length || 0} of {maxFiles} files selected
          {value?.filter(f => f?.uploaded)?.length > 0 && (
            <span className="text-green-600 ml-2">
              • {value?.filter(f => f?.uploaded)?.length} uploaded
            </span>
          )}
        </div>
      )}
      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-500 mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

export default ImageUpload;